import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  useTheme,
  alpha,
  Autocomplete,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import producerService, { Producer, PaginatedResponse, ProducerCountry } from '../../../services/producerService';
import { useAuth } from '../../../context/AuthContext';
import { getCountryName } from '../../../utils/countryUtils';
import ImportExportModal from '../../../components/ImportExportModal';
import { producerConfig } from '../../../utils/importExportConfigs';

const ProducerList: React.FC = () => {
  const navigate = useNavigate();
  const { isSystemAdmin } = useAuth();
  const theme = useTheme();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [producers, setProducers] = useState<Producer[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [availableCountries, setAvailableCountries] = useState<ProducerCountry[]>([]);
  
  const [pagination, setPagination] = useState({
    page: 0, // MUI uses 0-indexed pages
    pageSize: 25,
    totalItems: 0,
    totalPages: 0
  });

  const [sortField, setSortField] = useState('fullName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [reviewFilter, setReviewFilter] = useState<'all' | 'needs_review' | 'complete'>('all');
  const [importExportOpen, setImportExportOpen] = useState(false);

  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce((query: string) => {
      setSearchQuery(query);
    }, 300),
    []
  );

  // Function to fetch producers based on current state
  const fetchProducers = useCallback(async () => {
    setLoading(true);
    try {
      const needsReview = reviewFilter === 'needs_review' ? true : reviewFilter === 'complete' ? false : undefined;
      const response = await producerService.getAllProducers(
        pagination.page + 1, // API uses 1-indexed pages
        pagination.pageSize,
        sortField,
        sortOrder,
        searchQuery,
        selectedCountries.length > 0 ? selectedCountries : undefined,
        needsReview
      );
      
      // Client-side safety net in case backend ignores the filter param
      const filtered =
        reviewFilter === 'needs_review'
          ? response.data.filter((p) => p.needsReview === true)
          : reviewFilter === 'complete'
          ? response.data.filter((p) => !p.needsReview)
          : response.data;
      setProducers(filtered);
      setPagination({
        ...pagination,
        totalItems: response.pagination.totalItems,
        totalPages: response.pagination.totalPages
      });
    } catch (error) {
      console.error('Failed to fetch producers:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, sortField, sortOrder, searchQuery, selectedCountries, reviewFilter]);

  // Fetch available countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countries = await producerService.getAvailableCountries();
        setAvailableCountries(countries);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      }
    };
    fetchCountries();
  }, []);

  // Load producers when parameters change
  useEffect(() => {
    fetchProducers();
  }, [fetchProducers]);

  // Handle edit producer
  const handleEditProducer = useCallback((id: string) => {
    navigate(`/admin/producers/edit/${id}`);
  }, [navigate]);

  // Handle accept (resolve needsReview)
  const handleAcceptProducer = useCallback(async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await producerService.updateProducer(id, { needsReview: false });
      setProducers(prev => prev.map(p => p._id === id ? { ...p, needsReview: false } : p));
    } catch {
      alert('Failed to accept. Please try again.');
    }
  }, []);

  // Handle delete producer
  const handleDeleteProducer = useCallback(async (id: string) => {
    const producer = producers.find(p => p._id === id);
    if (!producer) return;
    
    if (window.confirm(`Are you sure you want to delete the producer "${producer.fullName || producer.name}"?`)) {
      try {
        await producerService.deleteProducer(id);
        fetchProducers(); // Refresh the list
      } catch (error) {
        console.error('Failed to delete producer:', error);
        alert('Failed to delete producer. Please try again.');
      }
    }
  }, [producers, fetchProducers]);

  // Handle search input change
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    debouncedSearch(value);
  }, [debouncedSearch]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Handle country filter change
  const handleCountryFilterChange = useCallback((event: any, newValue: string[]) => {
    setSelectedCountries(newValue);
  }, []);

  // Handle page change
  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPagination({ ...pagination, page: newPage });
  }, [pagination]);

  // Handle rows per page change
  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination({
      ...pagination,
      pageSize: parseInt(event.target.value, 10),
      page: 0 // Reset to first page
    });
  }, [pagination]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Search and Filters */}
        <Box sx={{ 
          mb: 2, 
          display: 'flex', 
          gap: 2, 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
        }}>
          <TextField
            placeholder="Search producers..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ 
              flexGrow: 1, 
              maxWidth: { xs: '100%', sm: 400 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.background.paper,
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    <ClearIcon fontSize="small" />
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          
          <Autocomplete
            multiple
            options={availableCountries.map(c => c.country)}
            getOptionLabel={(option) => getCountryName(option)}
            value={selectedCountries}
            onChange={handleCountryFilterChange}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Filter by country"
                size="small"
                sx={{ minWidth: 200 }}
              />
            )}
            renderTags={(value, getTagProps) => (
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                {value.map((option, index) => {
                  const { key, ...other } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={getCountryName(option)}
                      size="small"
                      {...other}
                    />
                  );
                })}
              </Stack>
            )}
            sx={{ minWidth: { xs: '100%', sm: 250 } }}
          />
          
          <Select
            value={reviewFilter}
            onChange={(e) => setReviewFilter(e.target.value as typeof reviewFilter)}
            sx={{ minWidth: 130, '& .MuiSelect-select': { py: '6.5px', fontSize: '0.875rem' } }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="needs_review">Needs Review</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
          </Select>

          <Button
            variant="outlined"
            onClick={() => setImportExportOpen(true)}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            Import / Export
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/producers/create')}
            sx={{ minWidth: 'auto', px: 3 }}
          >
            Add Producer
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : producers.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ p: 3 }}>
            No producers found. Click "Add Producer" to create one.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Producer</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {producers.map((producer) => (
                    <TableRow
                      key={producer._id}
                      hover
                      onClick={() => handleEditProducer(producer._id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: producer.needsReview ? alpha(theme.palette.error.main, 0.06) : 'inherit',
                        '&:hover': {
                          backgroundColor: producer.needsReview
                            ? alpha(theme.palette.error.main, 0.12)
                            : 'rgba(0, 0, 0, 0.04)',
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {producer.fullName || producer.name}
                            </Typography>
                            {producer.title && (
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {producer.title}
                              </Typography>
                            )}
                          </Box>
                          {producer.needsReview && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title="Needs review">
                                <ReviewIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                              </Tooltip>
                              <Chip
                                label="Accept"
                                size="small"
                                color="success"
                                variant="outlined"
                                onClick={(e) => handleAcceptProducer(e, producer._id)}
                                sx={{ cursor: 'pointer', height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getCountryName(producer.country)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: producer.region ? 'inherit' : theme.palette.text.secondary }}>
                          {producer.region || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit">
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProducer(producer._id);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            color="error" 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProducer(producer._id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={pagination.totalItems}
              page={pagination.page}
              onPageChange={handleChangePage}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
      <ImportExportModal
        open={importExportOpen}
        onClose={() => { setImportExportOpen(false); fetchProducers(); }}
        config={producerConfig}
      />
    </Box>
  );
};

export default ProducerList;